"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { Search, Lightbulb, ShieldCheck, Phone, Home, FileText } from "lucide-react";
const steps = [{
  title: "Prise de contact et collecte des documents",
  description: "Echange initial avec le client pour comprendre ses besoins, sa situation (vente, projet de travaux, démarche volontaire), et déterminer le type d'audit adapté (réglementaire ou volontaire). Recueil des informations nécessaires avant la visite (plans du logement si disponibles, dernières factures énergétiques, DPE existant, année de construction, nature des équipements en place). Une bonne préparation : gage d'une bonne prestation !"
}, {
  title: "Visite technique sur site",
  description: "Inspection complète du logement par un expert NSDB certifié RGE : analyse de l'enveloppe du bâtiment (murs, toiture, planchers, menuiseries), des systèmes de chauffage, de ventilation et de production d'eau chaude sanitaire, relevé des caractéristiques techniques et prise de mesures. Cette phase est cruciale : il n'y a pas d'audit pertinent sans un diagnostic approfondi et rigoureux !"
}, {
  title: "Analyse et modélisation énergétique",
  description: "Traitement des données collectées, modélisation thermique du logement, calcul des consommations énergétiques réelles et identification des postes de déperdition prioritaires. On rentre dans le vif des calculs : projection du plan de la maison et simulations thermiques en nombre !"
}, {
  title: "Elaboration du rapport d'audit",
  description: "Rédaction d'un rapport complet comprenant l'état des lieux énergétique du logement, des scénarios de travaux priorisés et chiffrés, les économies d'énergie estimées pour chaque scénario, et les aides financières mobilisables (MaPrimeRénov', CEE, éco-PTZ, etc.)."
}, {
  title: "Restitution et conseils",
  description: "Présentation des résultats au client, explication des préconisations, réponses aux questions et conseils personnalisés pour accompagner le client dans ses futurs choix."
}, {
  title: "Accompagnement post-audit (en option)",
  description: "Suivi du client dans ses démarches administratives pour l'obtention des aides financières, orientation vers des entreprises de travaux si nécessaire."
}];
function StepItem({
  step,
  index
}: {
  step: (typeof steps)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const icons = [Phone, Home, Search, Lightbulb, ShieldCheck, FileText];
  const Icon = icons[index];
  return <div ref={ref} data-index={index} className="relative flex flex-col bg-card border border-secondary/20 rounded-[12px] shadow-sm transition-all duration-700 ease-out opacity-100 translate-y-0 ![padding:16px_32px_16px_32px]">
      <div className="flex items-center justify-between ![margin:0px_0px_10px_0px]">
        <div className="p-3 bg-secondary/10 rounded-[8px]">
          {Icon && <Icon className="w-8 h-8 text-secondary" strokeWidth={1.5} />}
        </div>
        <span className="text-4xl font-bold font-sans select-none !text-[hsl(208,7%,46%)] !border-1 border-[rgb(72,86,106)] !border-none !shadow-none">
          0{index + 1}
        </span>
      </div>
      <h3 className="text-xl font-semibold mb-4 font-sans text-primary tracking-tight">
        {step.title}
      </h3>
      <p className="text-muted-foreground text-base leading-relaxed font-sans">
        {step.description}
      </p>
    </div>;
}
export default function LesEtapesDeLaudit() {
  return <section id="les-étapes-de-laudit" className="bg-muted/30 overflow-hidden ![padding:60px_0px_60px_0px]">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl text-center ![margin:0px_320px_50px_320px]">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6 font-sans tracking-tight">Les étapes de votre audit&nbsp;</h2>
          <p className="text-lg text-muted-foreground font-sans max-w-2xl mx-auto">Une approche structurée en 6 étapes clés pour garantir la précision de nos diagnostics et la pertinence de nos recommandations.</p>
        </div>
        <div className="grid grid-cols-1 gap-8 max-w-3xl mx-auto">
          {steps.map((step, i) => <StepItem key={i} step={step} index={i} />)}
        </div>
      </div>
    </section>;
}